import React, { useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { Field, FormikProps } from 'formik'
import { FileCopyOutlined } from '@material-ui/icons'
import { IconButton } from '@material-ui/core'
import get from 'lodash/get'
import { EditionStatus } from '@graphics/gfx-common/dist/constants'
import { IPackInput } from '../../types/pack'
import { triggerNotification } from '../../actions/notifications'
import {
  isEditionModified,
  createLanguageTransform,
} from '../../services/packForm'
import { useEditionTrackingChanges, useShowRevisionCopyAction } from './hooks'
import { FlexContainer, Space } from '../../layouts'
import { CustomTextField, CustomSelectField, P } from '../../components'
import { usePublishedPackChanged } from '../UpdatePackForm/hooks'

export interface IEditionRevisionFragment {
  index: number
  formikProps: FormikProps<IPackInput>
  isFormDisabled?: boolean
  isEditionPublished?: boolean
  dispatch: Dispatch
}

export const EditionRevisionFragmentComponent = ({
  index,
  formikProps,
  isFormDisabled = false,
  dispatch,
}: IEditionRevisionFragment) => {
  const isEditionChanged = get(
    formikProps.values,
    `editions.${index}.publishing.publishedDataChanged`
  )
  const showRevisionCopyAction = useShowRevisionCopyAction(formikProps, index)
  const isEditStatusNone = useCallback(
    () =>
      get(formikProps.values, `editions.${index}.editStatus`) ===
      EditionStatus.None,
    [formikProps.values, index]
  )

  const validateReleaseNote = (values) => (value: string) =>
    isReleaseNoteRequired() && !value ? 'The field is required' : ''

  const isReleaseNoteRequired = useCallback(
    () =>
      [EditionStatus.Update, EditionStatus.Correction].includes(
        get(formikProps.values, `editions.${index}.editStatus`) as EditionStatus
      ),
    [index, formikProps.values]
  )

  const isCurrentEditionModified = useEditionTrackingChanges(formikProps, index)
  const isPackModified = usePublishedPackChanged(formikProps)
  const { validateForm } = formikProps
  const handleCopyRevisionInfo = () => {
    const editStatus = get(formikProps.values, `editions.${index}.editStatus`)
    const releaseNote = get(formikProps.values, `editions.${index}.releaseNote`)
    get(formikProps.values, 'editions', []).forEach((item, i) => {
      if (
        item.publishing.published &&
        (item.publishing.publishedDataChanged ||
          isPackModified ||
          isEditionModified(
            get(formikProps.initialValues, `editions.${i}`, {}),
            get(formikProps.values, `editions.${i}`),
            {
              language: createLanguageTransform(formikProps.values.language),
            }
          ))
      ) {
        formikProps.setFieldValue(`editions.${i}.editStatus`, editStatus)
        formikProps.setFieldValue(`editions.${i}.releaseNote`, releaseNote)
      }
    })
    dispatch(triggerNotification('Copied revision info'))
  }

  useEffect(() => {
    // For some reason, after hiding revision fields form is not revalidating
    validateForm()
  }, [isCurrentEditionModified, validateForm])

  return isCurrentEditionModified || isPackModified || isEditionChanged ? (
    <>
      <Field
        multiline
        name={`editions.${index}.editStatus`}
        label="Revision Type"
        component={CustomSelectField}
        validateImmediately
        disabled={!!isFormDisabled}
        validate={(value) =>
          value === EditionStatus.None ? 'Select revision type' : undefined
        }
      >
        {[
          {
            value: EditionStatus.None,
            label: 'Select revision type',
          },
          {
            value: EditionStatus.Update,
            label: 'Update',
          },
          {
            value: EditionStatus.Correction,
            label: 'Correction',
          },
          {
            value: EditionStatus.Refresh,
            label: 'Refresh',
          },
        ]}
      </Field>
      <Field
        multiline
        trimValueOnBlur
        name={`editions.${index}.releaseNote`}
        label="Revision Note"
        component={CustomTextField}
        validateImmediately
        validate={validateReleaseNote(formikProps.values)}
        disabled={!!isFormDisabled || isEditStatusNone()}
      />
      {showRevisionCopyAction && (
        <Space pb={4}>
          <FlexContainer>
            <IconButton
              id="copy-revision-note"
              onClick={handleCopyRevisionInfo}
              disabled={!!isFormDisabled}
            >
              <FileCopyOutlined
                color={!!isFormDisabled ? 'disabled' : 'primary'}
              />
            </IconButton>
            <P>Copy Revision Info For All Updates</P>
          </FlexContainer>
        </Space>
      )}
    </>
  ) : null
}

export const EditionRevisionFragment = connect()(
  EditionRevisionFragmentComponent
)
